// src/pages/PesquisaJurisprudencia/hooks/useCasoTab.js
import { useState, useCallback } from 'react';
import { API_BASE_URL, PAGINACAO_INICIAL } from '../utils/constants';

const useCasoTab = ({ onResultados, onPaginacao, onResetBusca }) => {
  const [casos, setCasos] = useState([]);
  const [casoSelecionado, setCasoSelecionado] = useState(null);
  const [loadingCasos, setLoadingCasos] = useState(false);
  const [loadingPesquisaCaso, setLoadingPesquisaCaso] = useState(false);
  const [loadingAssociar, setLoadingAssociar] = useState(false);

  const carregarCasos = useCallback(async () => {
    setLoadingCasos(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/casos/todos?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setCasos(response.ok ? (await response.json()).data?.casos || [] : []);
    } catch (e) {
      console.error('Erro ao carregar casos:', e);
      setCasos([]);
    } finally {
      setLoadingCasos(false);
    }
  }, []);

  const carregarDetalheCaso = useCallback(async (casoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/casos/${casoId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setCasoSelecionado(response.ok ? (await response.json()).data : null);
    } catch (e) {
      console.error('Erro ao carregar detalhes do caso:', e);
      setCasoSelecionado(null);
    }
  }, []);

  const handleCasoChange = useCallback((casoId) => {
    if (casoId) {
      carregarDetalheCaso(casoId);
      onResetBusca();
    } else {
      setCasoSelecionado(null);
    }
  }, [carregarDetalheCaso, onResetBusca]);

  const encontrarJurisprudenciaPorCaso = useCallback(async () => {
    if (!casoSelecionado?.description) {
      alert('Selecione um caso válido para pesquisa');
      return;
    }
    setLoadingPesquisaCaso(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/jurisprudencia/encontrar-por-caso`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: casoSelecionado.description })
      });
      if (response.ok) {
        const data = await response.json();
        onResultados(data.data || []);
        onPaginacao(data.pagination || PAGINACAO_INICIAL);
      } else {
        alert('Erro ao realizar pesquisa por caso');
      }
    } catch (e) {
      console.error('Erro na pesquisa por caso:', e);
      alert('Erro ao realizar pesquisa por caso');
    } finally {
      setLoadingPesquisaCaso(false);
    }
  }, [casoSelecionado, onResultados, onPaginacao]);

  const associarJurisprudenciaAoCaso = useCallback(async (itemSelecionado) => {
    if (!itemSelecionado || !casoSelecionado) {
      alert('Erro: Dados necessários não encontrados');
      return;
    }
    const confirmar = window.confirm(
      `Deseja associar a jurisprudência "${itemSelecionado.processo_numero || itemSelecionado.id}" ao caso "${casoSelecionado.name || `Caso ${casoSelecionado.id}`}"?`
    );
    if (!confirmar) return;

    setLoadingAssociar(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/jurisprudencia/associar-ao-caso`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_jurisprudencia: parseInt(itemSelecionado.id),
          id_law_case: parseInt(casoSelecionado.id)
        })
      });
      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        const text = await response.text();
        console.error('Resposta não é JSON:', text);
        throw new Error('Resposta do servidor não é JSON válido');
      }
      const data = await response.json();
      if (response.ok && data.success) {
        alert('✅ Jurisprudência associada ao caso com sucesso!');
      } else {
        alert(data.message || 'Erro ao associar jurisprudência ao caso');
      }
    } catch (e) {
      console.error('Erro ao associar jurisprudência ao caso:', e);
      if (e.name === 'SyntaxError' && e.message.includes('JSON')) {
        alert('Erro: Resposta inválida do servidor. Verifique se o backend está funcionando corretamente.');
      } else {
        alert('Erro ao associar jurisprudência ao caso: ' + e.message);
      }
    } finally {
      setLoadingAssociar(false);
    }
  }, [casoSelecionado]);

  const getState = useCallback(() => ({
    casos,
    casoSelecionado
  }), [casos, casoSelecionado]);

  const restoreState = useCallback((estado) => {
    setCasos(estado.casos || []);
    setCasoSelecionado(estado.casoSelecionado || null);
  }, []);

  return {
    casos,
    casoSelecionado,
    loadingCasos,
    loadingPesquisaCaso,
    loadingAssociar,
    carregarCasos,
    handleCasoChange,
    encontrarJurisprudenciaPorCaso,
    associarJurisprudenciaAoCaso,
    getState,
    restoreState
  };
};

export default useCasoTab;